import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface LoyaltyPoints {
  id: string;
  user_id: string;
  points: number;
  points_earned: number;
  points_redeemed: number;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  created_at: string;
  updated_at: string;
}

export const useLoyaltyPoints = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: loyaltyData, isLoading } = useQuery({
    queryKey: ['loyalty-points', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('loyalty_points')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No loyalty record exists, create one
          const { data: newData, error: createError } = await supabase
            .from('loyalty_points')
            .insert({
              user_id: user.id,
              points: 0,
              points_earned: 0,
              points_redeemed: 0,
              tier: 'bronze',
            })
            .select()
            .single();

          if (createError) throw createError;
          return newData;
        }
        throw error;
      }

      return data;
    },
    enabled: !!user,
  });

  const redeemPoints = useMutation({
    mutationFn: async (pointsToRedeem: number) => {
      if (!user || !loyaltyData) throw new Error('User not authenticated');
      if (pointsToRedeem > loyaltyData.points) throw new Error('Insufficient points');

      const { data, error } = await supabase
        .from('loyalty_points')
        .update({
          points: loyaltyData.points - pointsToRedeem,
          points_redeemed: loyaltyData.points_redeemed + pointsToRedeem,
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loyalty-points'] });
      toast.success('Points redeemed successfully!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to redeem points');
    },
  });

  const getTierInfo = (points: number) => {
    if (points >= 10000) return { tier: 'platinum', nextTier: null, pointsToNext: 0, discount: 20 };
    if (points >= 5000) return { tier: 'gold', nextTier: 'platinum', pointsToNext: 10000 - points, discount: 15 };
    if (points >= 1000) return { tier: 'silver', nextTier: 'gold', pointsToNext: 5000 - points, discount: 10 };
    return { tier: 'bronze', nextTier: 'silver', pointsToNext: 1000 - points, discount: 5 };
  };

  const pointsToDiscount = (points: number) => {
    // 100 points = £1 discount
    return points / 100;
  };

  return {
    loyaltyData,
    isLoading,
    redeemPoints: redeemPoints.mutate,
    isRedeeming: redeemPoints.isPending,
    getTierInfo,
    pointsToDiscount,
  };
};